/**
 * What: Formatter functions owning merchant-facing Shopify component phrases that interpolate values.
 * Transport / data-flow: Pure string builders; no React, Worker, D1, Shopify, or provider calls.
 *
 * Copy with a hole in it cannot live in the flat string dictionary, so it lives here as a
 * named function that owns the whole phrase. Keep entire sentences intact: assembling one
 * from a dictionary key plus an inline fragment is how this codebase shipped
 * "Backfill mode:full backfill" and "Thereis 1 issueto resolve".
 */
import { shopifyComponentCopy } from './shopify-component-copy.js';
/** Formats the Shopify study builder's survey-authoring issue-count banner sentence. */
export function formatShopifyStudyBuilderIssueCountMessage(errorCount) {
    const issuePhrase = errorCount === 1 ? 'is 1 issue' : `are ${errorCount.toLocaleString()} issues`;
    return `There ${issuePhrase} to resolve before you can prepare the test.`;
}
/** Formats the optional suggested-route hint appended to a Shopify diagnostic-question rubric finding. */
export function formatDiagnosticQuestionSuggestedRouteHint(suggestedRoute) {
    return ` (Consider changing to ${suggestedRoute} test type)`;
}
/** Formats the Shopify micro-survey choice icon field label from the choice's ordinal label. */
export function formatMicroSurveyChoiceIconLabel(choiceLabel) {
    return `${choiceLabel} icon`;
}
/** Formats the Shopify micro-survey choice interpretation field label from the choice's ordinal label. */
export function formatMicroSurveyChoiceInterpretationLabel(choiceLabel) {
    return `${choiceLabel} interpretation`;
}
/** Formats the Shopify micro-survey choice interpretation-value field label from the choice's ordinal label. */
export function formatMicroSurveyChoiceInterpretationValueLabel(choiceLabel) {
    return `${choiceLabel} interpretation value`;
}
/** Formats the Shopify all-responses browser's rating badge text. */
export function formatShopifyResponseRatingLabel(ratingDisplay) {
    return `Rating: ${ratingDisplay}`;
}
/** Formats the Shopify test result overview's directional confidence range sentence. */
export function formatShopifyStudyResultConfidenceRangeLabel(t2bCiLow, t2bCiHigh) {
    return `${t2bCiLow}-${t2bCiHigh}% directional range`;
}
/** Formats the Shopify study result overview's simulated-shopper sample size sentence. */
export function formatShopifyStudyResultSampleSizeLabel(sampleSize) {
    return `${sampleSize.toLocaleString()} simulated shoppers`;
}
/** Formats the Shopify segments tab's audience-group count badge. */
export function formatShopifySegmentsAudienceGroupCountLabel(segmentCount) {
    return `${segmentCount.toLocaleString()} audience groups`;
}
/** Formats the Shopify segments tab's per-group profiles heading from the group's segment label. */
export function formatShopifySegmentProfilesHeading(segmentLabel) {
    return `${segmentLabel} profiles`;
}
/** Formats the Shopify shopper intelligence relationship map's overlap diagram aria-label. */
export function formatShopifyRelationshipOverlapAriaLabel(primaryLabel, relatedLabel) {
    return `${primaryLabel} and ${relatedLabel} overlap`;
}
/** Formats the Shopify shopper intelligence panel's backfill-mode sentence. */
export function formatShopifyStoreIntelligenceBackfillModeLabel(forceBackfill) {
    return `${shopifyComponentCopy.storeIntelligenceStoreIntelligencePanel.backfillMode014} ${forceBackfill ? 'full backfill' : 'incremental'}`;
}
/** Formats the Shopify audience preview panel's sample-size summary sentence, branching on whether store-pattern background is applied. */
export function formatShopifyAudiencePreviewSampleSizeSummary(params) {
    const suffix = params.hasStorePatternBackground ? 'shoppers, shaped by your store data' : 'general shoppers';
    return `${params.sampleSize.toLocaleString()} ${suffix}`;
}
/** Merchant label for a Shopify product status; null for a missing or unrecognised status so no badge shows. */
export function formatShopifyProductStatusLabel(status) {
    if (status === 'ACTIVE')
        return shopifyComponentCopy.productDetails.statusActive;
    if (status === 'DRAFT')
        return shopifyComponentCopy.productDetails.statusDraft;
    if (status === 'ARCHIVED')
        return shopifyComponentCopy.productDetails.statusArchived;
    return null;
}
/** Formats the Shopify study activity banner's unread-report count title. */
export function formatShopifyStudyActivityBannerTitle(unreadCount) {
    return unreadCount === 1 ? 'New report ready' : `${unreadCount.toLocaleString()} new reports ready`;
}
/** Formats the Shopify test activity banner's unread-report body sentence. */
export function formatShopifyStudyActivityBannerBody(unreadCount) {
    return unreadCount === 1 ? 'Your test report is ready to view.' : 'Your test reports are ready to view.';
}
/** Formats the Shopify study activity banner's action label. */
export function formatShopifyStudyActivityBannerAction(unreadCount) {
    return unreadCount === 1 ? 'View report' : 'View a report';
}
/** Formats the Shopify technical preflight details grounding-audit row-count badge. */
export function formatShopifyGroundingRowsCountLabel(groundingRowCount) {
    return `${groundingRowCount.toLocaleString()} grounding rows`;
}
/** Formats the Shopify Shopper Background tag selector's search field label from the category title. */
export function formatShopperBackgroundSearchFieldLabel(categoryTitle) {
    return `Search ${categoryTitle}`;
}
/** Complete sentences only — never assembled from fragments (copy lint cannot see fragments). */
export function formatCustomerGroupFreshness(dateLabel) {
    return `Store data as of ${dateLabel}. Groups shift as Shopify recalculates, so shares refresh when a test is created.`;
}
/** Formats the measured Audience Studio composition legend row. */
export function formatShopifyAudienceCompositionLegendLabel(groupLabel, count) {
    return `${groupLabel}: ${count} of 100`;
}
/** Formats the selected Shopper Background share shown beside the measured composition. */
export function formatShopifyAudienceCompositionChipLabel(chipLabel, share) {
    return `${chipLabel}: ${Math.round(share)} of 100 within this audience`;
}
/** Formats an accessible label for one measured composition dot. */
export function formatShopifyAudienceCompositionDotLabel(groupLabel, ringed) {
    return `${groupLabel} dot${ringed ? ', selected background' : ''}`;
}
/** Formats the Shopify wizard header's progress-bar aria-label sentence. */
export function formatShopifyWizardProgressAriaLabel(progressPercent) {
    return `Progress: ${Math.round(progressPercent)}%`;
}
/** Formats the Shopify customer-pattern card's match caption from support and eligible counts. */
export function formatCustomersMatchPattern(supportCount, eligibleCount) {
    return `${supportCount.toLocaleString()} customers match this pattern · ${eligibleCount.toLocaleString()} customers we had enough data to assess`;
}
/** Formats one pattern-summary row's observed-share caption from its numerator and denominator. */
export function formatPatternShare(numerator, denominator) {
    return `${numerator.toLocaleString()} of ${denominator.toLocaleString()} customers we had enough data to assess`;
}
/** Formats a 0..1 share as a whole-number percentage. */
export function formatSharePercent(share) {
    return `${Math.round(share * 100)}%`;
}
/** Formats the aria-label for expanding one pattern-summary row's measurement details. */
export function formatPatternSummaryExpandLabel(rowLabel) {
    return `Show measurement details for ${rowLabel}`;
}
/** Formats the aria-label for collapsing one pattern-summary row's measurement details. */
export function formatPatternSummaryCollapseLabel(rowLabel) {
    return `Hide measurement details for ${rowLabel}`;
}
/** Formats the aria-label for expanding one customer-pattern card. */
export function formatCustomerPatternExpandLabel(patternName) {
    return `Show details for ${patternName}`;
}
/** Formats the aria-label for collapsing one customer-pattern card. */
export function formatCustomerPatternCollapseLabel(patternName) {
    return `Hide details for ${patternName}`;
}
/** Formats the label on the credits bar for the credits this test will use. */
export function formatShopifyCreditBarThisTest(estimatedCredits) {
    return `Uses ${estimatedCredits.toLocaleString()}`;
}
/** Formats the label on the credits bar for the credits available right now. */
export function formatShopifyCreditBarAvailable(availableCredits) {
    return `${availableCredits.toLocaleString()} for use`;
}
/** Formats the credits bar label shown when this test needs more credits than are available. */
export function formatShopifyCreditBarShortBy(estimatedCredits, availableCredits) {
    return `Short by ${(estimatedCredits - availableCredits).toLocaleString()}`;
}
/** Formats the hover text and screen-reader sentence describing the credits bar; no estimate yet is allowed. */
export function formatShopifyCreditBarAriaLabel(estimatedCredits, availableCredits, monthlyCredits) {
    const available = `${availableCredits.toLocaleString()} of your ${monthlyCredits.toLocaleString()} monthly credits are available`;
    if (estimatedCredits === null)
        return available;
    return estimatedCredits > availableCredits
        ? `This test needs ${estimatedCredits.toLocaleString()} credits but only ${available}`
        : `${available}, and this test would use ${estimatedCredits.toLocaleString()}`;
}
/** Formats the signed integer credit-allowance change delta. */
export function formatShopifyCreditDelta(delta) {
    const unit = Math.abs(delta) === 1 ? 'credit' : 'credits';
    return delta > 0 ? `+${delta.toLocaleString()} ${unit}` : `${delta.toLocaleString()} ${unit}`;
}
/** Formats the study summary's version count. */
export function formatShopifyBasketVersionCount(versionCount) {
    return versionCount === 1 ? '1 version' : `${versionCount.toLocaleString()} versions`;
}
/** Formats the study summary's shopper line: the total with its per-version split, or just the per-version count when there is nothing to compare. */
export function formatShopifyBasketShoppers(shoppersPerVersion, versionCount, totalShoppers) {
    const perVersion = shoppersPerVersion.toLocaleString();
    return versionCount > 0 && totalShoppers > 0
        ? `${totalShoppers.toLocaleString()} simulated shoppers (${perVersion} per version × ${versionCount.toLocaleString()})`
        : `${perVersion} simulated shoppers per version`;
}
/** Joins summary facts into one line. */
export function joinShopifyBasketFacts(facts) {
    return facts.join(' · ');
}
/** Formats the study summary's product variant count. */
export function formatShopifyBasketVariantCount(variantCount) {
    return `${variantCount.toLocaleString()} variants`;
}
/** Formats a product price range when its variants are priced differently. */
export function formatShopifyBasketPriceRange(lowPrice, highPrice) {
    return `${lowPrice} – ${highPrice}`;
}
/** The variant section's footer when a search narrows the list: how many of the product's variants the filter matched. */
export function formatShopifyVersionSearchMatches(matchCount, totalCount, query) {
    return `${matchCount.toLocaleString()} of ${totalCount.toLocaleString()} variants match “${query}”.`;
}
/** Formats the product fields the merchant is changing in the test. */
export function formatShopifyBasketChangingFields(fieldLabels) {
    return `Changing: ${fieldLabels.join(', ')}`;
}
/** Formats the study summary's shopper-question count. */
export function formatShopifyBasketQuestionCount(questionCount) {
    return questionCount === 1 ? '1 shopper question' : `${questionCount.toLocaleString()} shopper questions`;
}
/** Formats an audience market fact. */
export function formatShopifyBasketAudienceMarket(marketCountry) {
    return `${marketCountry} market`;
}
/** Formats an audience age-range fact. */
export function formatShopifyBasketAudienceAges(minimumAge, maximumAge) {
    return `Ages ${minimumAge}–${maximumAge}`;
}
/** Formats the typical run time from recent completed tests. */
export function formatShopifyBasketTiming(lowMinutes, highMinutes) {
    return lowMinutes === highMinutes ? `Usually about ${lowMinutes} min` : `Usually ${lowMinutes}–${highMinutes} min`;
}
/** Formats the credits this test is expected to use; the confirmed amount is shown when the test is run. */
export function formatShopifyBasketCreditEstimate(estimatedCredits) {
    const unit = estimatedCredits === 1 ? 'credit' : 'credits';
    return `About ${estimatedCredits.toLocaleString()} ${unit} for this test`;
}
//# sourceMappingURL=shopify-component-formatters.js.map